/// <amd-module name="TFS/FeatureManagement/Components/FeaturePanel/FeaturePanel" />

import * as React from "react";

import {
    ContributedFeature,
    ContributedFeatureEnabledValue,
    ContributedFeatureSettingScope,
    ContributedFeatureState
} from "TFS/FeatureManagement/Contracts/FeatureManagement";
import { ICancelablePromise, makeCancelable } from "VSS/Core/Util/Promise";
import { IFeatureManagementPanelComponentProps, IFeatureManagementScope } from "./FeaturePanel.Props";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";

import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { ITooltipProps } from "VSSUI/TooltipEx";
import { getSafeId } from "VSSUI/Util";
import { Link } from "VSSUI/Link";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";
import { Toggle } from "VSSUI/Toggle";
import { VssComponent } from "VSS/Platform/Layout";
import { format } from "VSS/Core/Util/String";

import FeatureResources = require("TFS/FeatureManagement/Resources");

/**
 * SetFeatureState is a method used to udpate the state of a feature.
 */
type SetFeatureState = (
    feature: ContributedFeature,
    userScoped: boolean,
    settingScope: string,
    state: ContributedFeatureEnabledValue,
    reason?: string,
    reasonCode?: string
) => void;

/**
 *
 */
export interface IFeatureManagementPanelComponentState {
    selectedDevFeatures?: ContributedFeature[];
    selectedReleaseFeatures?: ContributedFeature[];
    selectedScope?: IFeatureManagementScope;
}

/**
 * The FeatuerManagementPanelComponent is used to show a set of features based on feature
 * scopes. The current user will be able to see all the values, and depending on his
 * permissions they will be able to set the values from some or all of the features.
 */
export class FeatureManagementPanelComponent extends VssComponent<IFeatureManagementPanelComponentProps, IFeatureManagementPanelComponentState> {
    private featurePromises?: ICancelablePromise<{ [featureId: string]: ContributedFeatureState }>[];
    private featureStates: { [featureId: string]: ObservableValue<ContributedFeatureState> } = {};

    private filter: ObservableValue<string> = new ObservableValue("");
    private scopeSelection: DropdownSelection;

    private allDevFeatures?: ContributedFeature[];
    private allReleaseFeatures?: ContributedFeature[];
    private scopes?: IFeatureManagementScope[];

    constructor(props: IFeatureManagementPanelComponentProps) {
        super(props);

        this.scopeSelection = new DropdownSelection();

        this.state = {};

        // Watch for featureStateUpdated notifications from the featureService
        this.observe([{ propertyName: "featureService", observer: this._onFeatureStateChanged, action: "featureStateUpdated" }]);
    }

    public render(): JSX.Element {
        const { selectedDevFeatures, selectedReleaseFeatures } = this.state;
        const { isHosted, scopeValues } = this.props;

        let { selectedScope } = this.state;
        if (!selectedScope && this.scopes && this.scopes.length > 0) {
            selectedScope = this.scopes[0];
            this.scopeSelection.select(0, 1);
        }

        return (
            <Panel
                lightDismiss={true}
                className="features-panel"
                titleProps={{ text: isHosted ? FeatureResources.ManageFeaturesHeader : FeatureResources.ManageFeaturesHeaderOnPrem }}
                onDismiss={this._closePanel}
            >
                <div className="feature-management-container title-xs">
                    {!this.scopes ? (
                        <div className="feature-message">{FeatureResources.Loading}</div>
                    ) : (
                        <>
                            <div>{isHosted ? FeatureResources.ManageFeaturesSubHeading : FeatureResources.ManageFeaturesSubHeadingOnPrem}</div>
                            {this.scopes.length <= 1 ? null : (
                                <Dropdown<IFeatureManagementScope>
                                    ariaLabel={FeatureResources.FeatureScope}
                                    className="feature-scope-picklist"
                                    calloutContentClassName="feature-scope-callout"
                                    selection={this.scopeSelection}
                                    onSelect={this._onScopeChanged}
                                    items={this.scopes!.map((s) => {
                                        return { data: s, id: s.displayName, text: s.displayName };
                                    })}
                                />
                            )}
                            {selectedScope && !selectedScope.canManage ? (
                                <MessageBar className="scope-no-write-permission-message" severity={MessageBarSeverity.Info}>
                                    {FeatureResources.NoFeatureScopeWritePermission}
                                </MessageBar>
                            ) : null}
                            {selectedReleaseFeatures && scopeValues && selectedScope && Object.keys(this.featureStates).length > 0 ? (
                                <FeaturesListComponent
                                    collectFeedback={isHosted}
                                    features={selectedReleaseFeatures ? [...selectedReleaseFeatures] : []}
                                    featureStates={this.featureStates}
                                    scope={selectedScope}
                                    setFeatureState={this._setFeatureState}
                                />
                            ) : (
                                <div className="feature-message">{FeatureResources.NoFeaturesAvailable}</div>
                            )}
                            {selectedDevFeatures && selectedDevFeatures.length && scopeValues && selectedScope ? (
                                <div className="dev-mode-features">
                                    <div className="dev-mode-label body-l" title={FeatureResources.DevModeFeaturesHeaderTooltip}>
                                        {FeatureResources.DevModeFeaturesHeader}
                                    </div>
                                    {this.getEnableableFeatures().length && (
                                        <div className="enable-all-features flex-row flex-center">
                                            <span>{FeatureResources.DevModeEnableAllLabel}&nbsp;</span>
                                            <Button subtle={true} onClick={this.enableAllFeatures} text={FeatureResources.DevModeEnableAllLinkText} />
                                        </div>
                                    )}
                                    <Observer filterText={this.filter}>
                                        {(props: { filterText: string }) => {
                                            let filteredDevFeatures = selectedDevFeatures;
                                            if (props.filterText && selectedDevFeatures) {
                                                const lowerCaseFilter = props.filterText.trim().toLocaleLowerCase();
                                                filteredDevFeatures = selectedDevFeatures.filter((feature) =>
                                                    this._isMatch(feature, lowerCaseFilter)
                                                );
                                            }
                                            return (
                                                selectedScope && (
                                                    <>
                                                        <FormItem label={FeatureResources.FilterFeatures}>
                                                            <TextField value={props.filterText} onChange={this.filterChange} />
                                                        </FormItem>

                                                        <FeaturesListComponent
                                                            collectFeedback={false}
                                                            features={filteredDevFeatures ? [...filteredDevFeatures] : []}
                                                            featureStates={this.featureStates}
                                                            scope={selectedScope}
                                                            setFeatureState={this._setFeatureState}
                                                        />
                                                    </>
                                                )
                                            );
                                        }}
                                    </Observer>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </Panel>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // Read the feature definition data from the feature service.
        this.props.featureService.getFeatureDefinitions(undefined, true, undefined, ["product"]).then((featureDefinitions) => {
            const scopesUsed: { [scope: string]: boolean } = {};
            const scopes = [];

            if (featureDefinitions) {
                // Make a copy of the public features and sort them by the display name.
                if (featureDefinitions.features) {
                    this.allReleaseFeatures = featureDefinitions.features.slice().sort((f1: ContributedFeature, f2: ContributedFeature) => {
                        return f1.name.localeCompare(f2.name, navigator.language);
                    });

                    for (const feature of this.allReleaseFeatures) {
                        for (const scope of feature.scopes) {
                            scopesUsed[scope.settingScope || ""] = true;
                        }
                    }
                }

                // Make a copy of the dev features and sort them by the display name.
                if (featureDefinitions.devModeFeatures) {
                    this.allDevFeatures = featureDefinitions.devModeFeatures.slice().sort((f1: ContributedFeature, f2: ContributedFeature) => {
                        return f1.name.localeCompare(f2.name, navigator.language);
                    });

                    for (const feature of this.allDevFeatures) {
                        for (const scope of feature.scopes) {
                            scopesUsed[scope.settingScope || ""] = true;
                        }
                    }
                }

                // Apply management rules to the scopes that were supplied.
                if (this.props.supportedScopes && featureDefinitions.managementScopes) {
                    for (const scope of this.props.supportedScopes) {
                        if (scopesUsed[scope.featureScope.settingScope]) {
                            const updatedScope = { ...scope };

                            // If the canManage property was not already defined, we will attempt to resolve it with
                            // the featureData returned by the service.
                            if (scope.hasOwnProperty("canManage")) {
                                updatedScope.canManage = scope.canManage;
                            } else {
                                updatedScope.canManage = featureDefinitions.managementScopes[scope.featureScope.settingScope];
                            }

                            scopes.push(updatedScope);
                        }
                    }
                }
            }

            // Save the set of all scopes needed for this view.
            this.scopes = scopes;

            // Select the first scope once we have the details about the scope resolved.
            // We dont have any states loaded yet so we wont be able to show the proper On/Off state.
            if (scopes && scopes.length) {
                let initialScope = scopes[0];

                // If we have an initial feature to select, make sure we open the panel to a scope
                // where that feature appears.
                if (this.props.initialScope) {
                    for (const scope of scopes) {
                        if (this._isSameScope(this.props.initialScope, scope.featureScope)) {
                            initialScope = scope;
                            break;
                        }
                    }
                }

                this._setSelectedScope(initialScope);
            }
        });
    }

    private _isMatch = (feature: ContributedFeature, lowerCaseFilterText: string): boolean => {
        return (
            feature.name.toLocaleLowerCase().indexOf(lowerCaseFilterText) >= 0 ||
            feature.description.toLocaleLowerCase().indexOf(lowerCaseFilterText) >= 0
        );
    };

    private _closePanel = (): void => {
        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };

    private enableAllFeatures = () => {
        let { selectedScope } = this.state;
        if (!selectedScope && this.scopes && this.scopes.length > 0) {
            selectedScope = this.scopes[0];
        }

        if (selectedScope) {
            const features = this.getEnableableFeatures();
            for (const feature of features) {
                this._setFeatureState(
                    feature,
                    selectedScope.featureScope.userScoped,
                    selectedScope.featureScope.settingScope,
                    ContributedFeatureEnabledValue.Enabled
                );
            }
        }
    };

    private filterChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this.filter.value = value;
    };

    private getEnableableFeatures(): ContributedFeature[] {
        const { selectedDevFeatures, selectedReleaseFeatures } = this.state;
        const allFeatures = [...(selectedReleaseFeatures || []), ...(selectedDevFeatures || [])];
        let enableableFeatures: ContributedFeature[] = [];
        if (this.featureStates) {
            enableableFeatures = allFeatures.filter((f) => {
                const state = this.featureStates[f.id];
                return state && state.value.state !== ContributedFeatureEnabledValue.Enabled && !state.value.overridden;
            });
        }
        return enableableFeatures;
    }

    private _isFeatureInScope = (feature: ContributedFeature, currentScope: ContributedFeatureSettingScope): boolean => {
        for (const featureScope of feature.scopes) {
            if (this._isSameScope(featureScope, currentScope)) {
                return true;
            }
        }

        return false;
    };

    private _isSameScope(scope1: ContributedFeatureSettingScope, scope2: ContributedFeatureSettingScope): boolean {
        return scope1.userScoped === scope2.userScoped && (scope1.settingScope || "") === (scope2.settingScope || "");
    }

    private _onFeatureStateChanged = (featureState: ContributedFeatureState): void => {
        /* @TODO: How do we handle errors being returned from these calls?
        let error = this.props.featureService.getFeatureStateUpdateError(this.props.feature.id, this.props.scope);
        errorText: error ? VSS.getErrorMessage(error) : null
*/
        if (this.featureStates[featureState.featureId]) {
            this.featureStates[featureState.featureId].value = featureState;
        }
    };

    private _onScopeChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IFeatureManagementScope>): void => {
        if (this.scopes && item && item.data) {
            // Clear any previous feature states since they were associated with a different scope.
            this.featureStates = {};
            this._setSelectedScope(item.data);
        }
    };

    private _setFeatureState = (
        feature: ContributedFeature,
        userScoped: boolean,
        settingScope: string,
        state: ContributedFeatureEnabledValue,
        reason?: string,
        reasonCode?: string
    ): void => {
        this.props.featureService.setFeatureState(feature, userScoped, settingScope, this.props.scopeValues[settingScope], state, reason, reasonCode);
    };

    private _setSelectedScope = (scope: IFeatureManagementScope) => {
        const selectedDevFeatures =
            this.allDevFeatures &&
            this.allDevFeatures.filter((feature, index) => {
                return this._isFeatureInScope(feature, scope.featureScope);
            });
        const selectedReleaseFeatures =
            this.allReleaseFeatures &&
            this.allReleaseFeatures.filter((feature, index) => {
                return this._isFeatureInScope(feature, scope.featureScope);
            });

        this.setState({
            selectedScope: scope,
            selectedDevFeatures: selectedDevFeatures,
            selectedReleaseFeatures: selectedReleaseFeatures
        });

        if (scope && this.scopes) {
            const selectedScopeIndex = this.scopes.findIndex((s) => this._isSameScope(s.featureScope, scope.featureScope));
            if (selectedScopeIndex >= 0 && !this.scopeSelection.selected(selectedScopeIndex)) {
                this.scopeSelection.select(selectedScopeIndex);
            }
        }

        // Cancel any pending feature promises that are still outstanding from a previous selection change.
        if (this.featurePromises) {
            for (const featurePromise of this.featurePromises) {
                featurePromise.cancel();
            }
        }

        this.featurePromises = [];

        // Build a single array with the release and development features we are showing.
        const selectedFeatures: ContributedFeature[] = new Array().concat(selectedReleaseFeatures || [], selectedDevFeatures || []);

        if (selectedFeatures.length > 0) {
            // Retrieve the feature states for the features we are currently showing.
            const featurePromises = this.props.featureService.getFeatureStates(
                selectedFeatures,
                scope.featureScope.userScoped,
                scope.featureScope.settingScope,
                this.props.scopeValues
            );
            for (const featurePromise of featurePromises) {
                // Make sure the returned promises are cancelable.
                const cancelablePromise = makeCancelable(featurePromise);
                this.featurePromises.push(cancelablePromise);

                // When the promise is complete we will re-render the feature lists.
                cancelablePromise.promise.then((featureStates) => {
                    // Create or update the ObservableValues for each of the resolved states.
                    for (const featureId in featureStates) {
                        if (this.featureStates[featureId]) {
                            this.featureStates[featureId].value = featureStates[featureId];
                        } else {
                            this.featureStates[featureId] = new ObservableValue(featureStates[featureId]);
                        }
                    }

                    this.forceUpdate();
                });
            }
        }
    };
}

interface IFeaturesListComponentProps {
    collectFeedback: boolean;
    features: ContributedFeature[];
    featureStates: { [featureId: string]: ObservableValue<ContributedFeatureState> };
    setFeatureState: SetFeatureState;
    scope: IFeatureManagementScope;
}

function FeaturesListComponent(props: IFeaturesListComponentProps): JSX.Element {
    const { collectFeedback, features, featureStates, scope, setFeatureState } = props;
    return (
        <div className="features-list body-m">
            {features.map((feature: ContributedFeature, index: number) => {
                return (
                    <FeatureComponent
                        collectFeedback={collectFeedback && feature.id.substr(0, 3) === "ms."}
                        feature={feature}
                        featureState={featureStates[feature.id]}
                        key={feature.id}
                        scope={scope}
                        setFeatureState={setFeatureState}
                    />
                );
            })}
        </div>
    );
}

interface IFeatureComponentProps {
    collectFeedback: boolean;
    feature: ContributedFeature;
    setFeatureState: SetFeatureState;
    scope: IFeatureManagementScope;
    featureState?: ObservableValue<ContributedFeatureState>;
}

interface IFeatureComponentState {
    errorText?: string;
    confirmScopeMessage?: string;
    confirmDialogTitle?: string;
    confirmButtonText?: string;
    collectFeedback?: boolean;
    hasBeenToggled?: boolean;
}

const FeaturePanelAllowContact = "AllowContact";

class FeatureComponent extends VssComponent<IFeatureComponentProps, IFeatureComponentState> {
    private _feedbackText = new ObservableValue("");
    private _allowContact = new ObservableValue<boolean>(false);

    constructor(props: IFeatureComponentProps) {
        super(props);

        this.state = {};
        this.observe([{ propertyName: "featureState", observer: this._onFeatureStateChanged }]);
    }

    public render(): JSX.Element {
        const { feature, featureState, scope } = this.props;

        let checked = false;
        let overridden = false;
        let reason: string | undefined = undefined;
        if (featureState && featureState.value) {
            checked = featureState.value.state === ContributedFeatureEnabledValue.Enabled;
            overridden = featureState.value.overridden;
            reason = featureState.value.reason;
        }

        // If there is a reason we will show it as a tooltip.
        const tooltipProps: ITooltipProps | undefined = reason ? { text: reason } : undefined;

        return (
            <div className="feature flex-grow">
                <div className="feature-header">
                    <div className="feature-name title-xs">{feature.name}</div>
                    <Toggle
                        aria-disabled={featureState ? undefined : true}
                        checked={checked}
                        className={featureState ? undefined : "loading"}
                        disabled={!scope.canManage || overridden}
                        offAriaLabel={format(FeatureResources.ToggleFeatureOffAriaLabelFormat, feature.name) + " " + feature.description}
                        offText={FeatureResources.ManageFeaturesOffLabel}
                        onAriaLabel={format(FeatureResources.ToggleFeatureOnAriaLabelFormat, feature.name) + " " + feature.description}
                        onChange={this._onToggleChanged}
                        onText={FeatureResources.ManageFeaturesOnLabel}
                        tooltipProps={tooltipProps}
                    />
                </div>
                <div className="feature-description">
                    <span>{feature.description}</span>
                    {feature._links && feature._links.learn ? (
                        <Link
                            href={feature._links.learn.href}
                            className="learn-more"
                            ariaLabel={format(FeatureResources.FeatureLearnMoreAriaLabel, feature.name)}
                            target="_blank"
                        >
                            {FeatureResources.LearnMore}
                        </Link>
                    ) : null}
                </div>
                {this.state.errorText ? (
                    <MessageBar
                        className="feature-toggle-error"
                        severity={MessageBarSeverity.Error}
                        onDismiss={() => {
                            this.setState({ errorText: undefined });
                        }}
                    >
                        {this.state.errorText}
                    </MessageBar>
                ) : null}
                {this.state.confirmDialogTitle ? (
                    <Dialog
                        defaultActiveElement={"#" + getSafeId("feature-feedback-input")}
                        calloutContentClassName="feature-feedback-dialog"
                        footerButtonProps={[
                            {
                                onClick: () => {
                                    this._closeConfirmDialog(false);
                                },
                                text: FeatureResources.CancelButtonText
                            },
                            {
                                primary: true,
                                onClick: () => {
                                    this._closeConfirmDialog(true);
                                },
                                text: this.state.confirmButtonText
                            }
                        ]}
                        lightDismiss={false}
                        onDismiss={() => {
                            this._closeConfirmDialog(false);
                        }}
                        titleProps={{ text: this.state.confirmDialogTitle }}
                    >
                        {this.state.confirmScopeMessage ? (
                            <MessageBar className="feature-toggle-error" severity={MessageBarSeverity.Warning}>
                                {this.state.confirmScopeMessage}
                            </MessageBar>
                        ) : null}
                        {this.state.collectFeedback ? (
                            <FormItem className="feature-feedback-text" label={FeatureResources.TurnOffFeatureDialogLabel}>
                                <TextField
                                    inputId="feature-feedback-input"
                                    multiline={true}
                                    onChange={this._onFeedbackChanged}
                                    value={this._feedbackText}
                                />
                                <Checkbox
                                    className="margin-top-4"
                                    label={FeatureResources.AllowMicrosoftToContactForDetails}
                                    onChange={this._onAllowContactChanged}
                                    checked={this._allowContact}
                                />
                                <Link
                                    className="margin-top-4"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={FeatureResources.TurnOffFeatureDialogPrivacyLink}
                                >
                                    {FeatureResources.TurnOffFeatureDialogPrivacyText}
                                </Link>
                            </FormItem>
                        ) : null}
                    </Dialog>
                ) : null}
            </div>
        );
    }

    private _closeConfirmDialog(toggle: boolean) {
        this.setState({
            collectFeedback: false,
            confirmButtonText: undefined,
            confirmDialogTitle: undefined,
            confirmScopeMessage: undefined
        });

        if (toggle && this.props.featureState && this.props.featureState.value) {
            const newState =
                this.props.featureState.value.state === ContributedFeatureEnabledValue.Enabled
                    ? ContributedFeatureEnabledValue.Disabled
                    : ContributedFeatureEnabledValue.Enabled;
            this._setFeatureState(newState, this._feedbackText.value, this._allowContact.value);
        }
    }

    private _onFeedbackChanged = (event: React.ChangeEvent<HTMLInputElement>, text: string): void => {
        this._feedbackText.value = text;
    };

    private _onAllowContactChanged = (
        event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>,
        checked: boolean
    ): void => {
        this._allowContact.value = checked;
    };

    private _onFeatureStateChanged = (featureState: ContributedFeatureState): void => {
        this.forceUpdate();
    };

    private _onToggleChanged = () => {
        if (!this.props.featureState || !this.props.featureState.value) {
            return;
        }

        const newState =
            this.props.featureState.value.state === ContributedFeatureEnabledValue.Enabled
                ? ContributedFeatureEnabledValue.Disabled
                : ContributedFeatureEnabledValue.Enabled;
        const turnOff = newState === ContributedFeatureEnabledValue.Disabled;
        const collectFeedback = turnOff && this.props.collectFeedback && !this.state.hasBeenToggled;

        // Clear out any previous feedback text from the toggling.
        this._feedbackText.value = "";

        const confirmScopeMessage = this.props.scope.scopeWarningText;
        if (collectFeedback || confirmScopeMessage) {
            this.setState({
                collectFeedback: collectFeedback,
                confirmDialogTitle: format(
                    turnOff ? FeatureResources.TurnOffFeatureDialogTitleFormat : FeatureResources.TurnOnFeatureDialogTitleFormat,
                    this.props.feature.name
                ),
                confirmScopeMessage: confirmScopeMessage,
                confirmButtonText: turnOff ? FeatureResources.TurnOffFeatureButtonText : FeatureResources.TurnOnFeatureButtonText
            });
        } else {
            this._setFeatureState(newState, undefined);
        }
    };

    private _setFeatureState(newState: ContributedFeatureEnabledValue, feedbackText?: string, allowContact?: boolean) {
        const {
            feature,
            scope: { featureScope }
        } = this.props;

        this.props.setFeatureState(
            feature,
            featureScope.userScoped,
            featureScope.settingScope,
            newState,
            feedbackText,
            allowContact ? FeaturePanelAllowContact : undefined
        );

        if (!this.state.hasBeenToggled) {
            // Remember that this feature has been toggled. We want to prevent showing the telemetry dialog
            // in the case that someone enables then disables the feature without closing the management UI.
            this.setState({ hasBeenToggled: true });
        }
    }
}
